import i18next from 'i18next';
import {from} from 'rxjs';

import * as Actions from 'web-app/react/dialog/actions';
import type * as Types from 'web-app/react/dialog/types';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import generateUUID from 'web-app/util/uuid';

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showDialog = (dialog: Types.PartialDialogProps) =>
    new Promise<Types.DialogChoice>(resolve =>
        Actions.show.dispatch(generateUUID(), {
            image: dialog.image,
            title: dialog.title,
            description: dialog.description,
            children: dialog.children,
            className: dialog.className,
            type: dialog.type,
            resolve: (value: Types.DialogChoice) => resolve(value),
            onDialogChoice: dialog.onDialogChoice,
            choices: [
                dialog.hideCancelButton || dialog.type === 'mf'
                    ? null
                    : {
                          appearance: 'Default',
                          text: dialog.cancelText || i18next.t('cancel'),
                          value: 'CANCEL',
                          e2eId: 'dialog-cancel-button',
                      },
                dialog.hideSubmitButton || dialog.type === 'mf'
                    ? null
                    : {
                          appearance: 'Primary',
                          text: dialog.okText || i18next.t('ok'),
                          value: 'OK',
                          e2eId: 'dialog-confirm-button',
                      },
                dialog.hideCancelButton || dialog.type !== 'mf'
                    ? null
                    : {
                          text: dialog.cancelText || i18next.t('cancel'),
                          variant: 'tertiary',
                          size: 'regular',
                          value: 'CANCEL',
                          e2eId: 'dialog-cancel-button',
                      },
                dialog.hideSubmitButton || dialog.type !== 'mf'
                    ? null
                    : {
                          text: dialog.okText || i18next.t('ok'),
                          variant: 'primary',
                          size: 'regular',
                          value: 'OK',
                          e2eId: 'dialog-confirm-button',
                      },
            ].filter(hasValue),
            ['data-e2e-id']: dialog['data-e2e-id'],
        }),
    );

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showAlert = (dialog: Omit<Types.PartialDialogProps, 'okText' | 'hideSubmitButton' | 'hideCancelButton'>) =>
    new Promise<'OK'>(resolve =>
        Actions.show.dispatch(generateUUID(), {
            title: dialog.title,
            description: dialog.description,
            children: dialog.children,
            resolve: (value: 'OK') => resolve(value),
            onDialogChoice: dialog.onDialogChoice,
            choices: [
                {
                    appearance: 'Primary',
                    text: dialog.cancelText || i18next.t('ok'),
                    value: 'OK',
                    e2eId: 'dialog-confirm-button',
                },
            ],
            ['data-e2e-id']: dialog['data-e2e-id'],
        }),
    );

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showDeleteDialog = (dialog: Omit<Types.PartialDialogProps, 'hideCancelButton' | 'hideSubmitButton'>) =>
    new Promise<Types.DialogChoice>(resolve =>
        Actions.show.dispatch(generateUUID(), {
            title: dialog.title,
            description: dialog.description,
            children: dialog.children,
            resolve: (value: Types.DialogChoice) => resolve(value),
            onDialogChoice: dialog.onDialogChoice,
            type: dialog.type,
            choices:
                dialog.type === 'mf'
                    ? [
                          {
                              text: dialog.cancelText || i18next.t('cancel'),
                              variant: 'tertiary',
                              size: 'regular',
                              value: 'CANCEL',
                              e2eId: 'dialog-cancel-button',
                          },
                          {
                              text: dialog.okText || i18next.t('delete'),
                              variant: 'critical',
                              size: 'regular',
                              value: 'OK',
                              e2eId: 'dialog-confirm-button',
                          },
                      ]
                    : [
                          {
                              appearance: 'Default',
                              text: dialog.cancelText || i18next.t('cancel'),
                              value: 'CANCEL',
                              e2eId: 'dialog-cancel-button',
                          },
                          {
                              appearance: 'Danger',
                              text: dialog.okText || i18next.t('delete'),
                              value: 'OK',
                              e2eId: 'dialog-confirm-button',
                          },
                      ],
            ['data-e2e-id']: dialog['data-e2e-id'],
        }),
    );

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showCustomDialog = <T>(dialog: Omit<Types.DialogProps<T>, 'resolve'>) =>
    new Promise<T>(resolve =>
        Actions.show.dispatch(generateUUID(), {
            ...dialog,
            resolve: (value: T) => resolve(value),
        }),
    );

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showOnPredicateDialog =
    (dialog: Types.OnPredicatesDialogProps) =>
    (...args: unknown[]) => {
        if (dialog.predicate) {
            showDialog(dialog).then(selection => {
                if (selection === 'OK') {
                    if ('func' in dialog) {
                        dialog.func(...args);
                    } else {
                        dialog.onPredicateTrueFunc(...args);
                    }
                }
            });
        } else if ('func' in dialog) {
            dialog.func(...args);
        } else {
            dialog.onPredicateFalseFunc(...args);
        }
    };

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showDeleteDialogObservable = (...args: Parameters<typeof showDeleteDialog>) =>
    from(showDeleteDialog(...args));

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showDialogObservable = (...args: Parameters<typeof showDialog>) => from(showDialog(...args));

/**
 * @deprecated use `useDialog` or `useGenericDialog` from '@famly/mf_modals-dialogs_context` instead
 * @see {@link file://./../../../famly/mf/modals-dialogs_context/README.md}
 */
export const showAlertObservable = (...args: Parameters<typeof showAlert>) => from(showAlert(...args));
